<template>
  <div class="derechos-page">
    <!-- Masthead compacto: kicker + titulo + stats inline (estilo Prensa) -->
    <header class="masthead">
      <div class="masthead-row">
        <div class="masthead-titles">
          <p class="masthead-kicker">
            {{ t("derechos.kicker") }}
          </p>
          <h1 class="masthead-title">
            {{ t("derechos.titulo") }}
          </h1>
        </div>
        <ul
          class="masthead-meta"
          :aria-label="t('derechos.resumenAria')"
        >
          <li>
            <strong>{{ secciones.length }}</strong>
            {{ t("derechos.metaSecciones") }}
          </li>
          <li aria-hidden="true">
            ·
          </li>
          <li>{{ t("derechos.metaActualizado") }}</li>
        </ul>
      </div>
    </header>

    <div class="dr-layout">
      <main class="dr-main">
        <p class="dr-lead">
          {{ t("derechos.intro") }}
        </p>

        <!-- Tabs editoriales -->
        <div
          class="editorial-tabs"
          role="tablist"
          :aria-label="t('derechos.tabs.aria')"
        >
          <button
            v-for="tab in tabs"
            :id="`tab-${tab.id}`"
            :key="tab.id"
            type="button"
            role="tab"
            class="editorial-tab"
            :class="{ 'is-active': activeTab === tab.id }"
            :aria-selected="activeTab === tab.id"
            :aria-controls="`panel-${tab.id}`"
            @click="activeTab = tab.id"
          >
            <span class="tab-num">{{ tab.num }}</span>
            <span class="tab-label">{{ tab.label }}</span>
          </button>
        </div>

        <!-- Panel I: Como funciona (quien + materias + fases) -->
        <section
          v-show="activeTab === 'como'"
          id="panel-como"
          role="tabpanel"
          aria-labelledby="tab-como"
          class="dr-panel"
        >
          <article
            id="quien"
            class="dr-section"
          >
            <div class="dr-section-head">
              <span
                class="dr-section-num"
                aria-hidden="true"
              >01</span>
              <div>
                <h2>{{ t("derechos.s1.h") }}</h2>
                <p class="dr-section-intro">
                  {{ t("derechos.s1.intro") }}
                </p>
              </div>
            </div>

            <div class="dr-callout">
              <p class="dr-callout-eyebrow">
                <v-icon
                  size="16"
                  aria-hidden="true"
                >
                  mdi-information-outline
                </v-icon>
                {{ t("derechos.s1.cardTitulo") }}
              </p>
              <p
                class="dr-callout-text"
                v-html="t('derechos.s1.cardCuerpo')"
              />
            </div>
          </article>

          <article
            id="materias"
            class="dr-section"
          >
            <div class="dr-section-head">
              <span
                class="dr-section-num"
                aria-hidden="true"
              >02</span>
              <div>
                <h2>{{ t("derechos.s2.h") }}</h2>
                <p class="dr-section-intro">
                  {{ t("derechos.s2.intro") }}
                </p>
              </div>
            </div>

            <div class="dr-materias">
              <div class="dr-materias-col is-si">
                <p class="dr-materias-head">
                  <v-icon
                    size="18"
                    aria-hidden="true"
                  >
                    mdi-check-circle
                  </v-icon>
                  {{ t("derechos.s2.tituloSi") }}
                </p>
                <ul>
                  <li
                    v-for="item in itemsSi"
                    :key="item"
                  >
                    {{ item }}
                  </li>
                </ul>
              </div>
              <div class="dr-materias-col is-no">
                <p class="dr-materias-head">
                  <v-icon
                    size="18"
                    aria-hidden="true"
                  >
                    mdi-close
                  </v-icon>
                  {{ t("derechos.s2.tituloNo") }}
                </p>
                <ul>
                  <li
                    v-for="item in itemsNo"
                    :key="item"
                  >
                    {{ item }}
                  </li>
                </ul>
              </div>
            </div>
          </article>

          <article
            id="fases"
            class="dr-section dr-section--last"
          >
            <div class="dr-section-head">
              <span
                class="dr-section-num"
                aria-hidden="true"
              >03</span>
              <div>
                <h2>{{ t("derechos.s3.h") }}</h2>
                <p class="dr-section-intro">
                  {{ t("derechos.s3.intro") }}
                </p>
              </div>
            </div>

            <ol class="dr-timeline">
              <li
                v-for="(fase, i) in fases"
                :key="fase.id"
                class="dr-timeline-item"
              >
                <div
                  class="dr-timeline-marker"
                  aria-hidden="true"
                >
                  <span class="dr-timeline-num">{{ String(i + 1).padStart(2, '0') }}</span>
                </div>
                <div class="dr-timeline-body">
                  <p class="dr-timeline-eyebrow">
                    {{ fase.label }}
                  </p>
                  <h3>{{ fase.titulo }}</h3>
                  <ul
                    v-if="fase.meta?.length"
                    class="dr-timeline-meta"
                  >
                    <li
                      v-for="(m, idx) in fase.meta"
                      :key="idx"
                    >
                      <span>{{ m.label }}</span>
                      <strong>{{ m.value }}</strong>
                    </li>
                  </ul>
                  <p
                    v-for="(parr, idx) in fase.parrafos"
                    :key="idx"
                    v-html="parr"
                  />
                  <a
                    v-if="fase.cta"
                    :href="fase.cta.href"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="dr-cta"
                  >
                    {{ fase.cta.texto }}
                    <v-icon
                      size="14"
                      aria-hidden="true"
                    >mdi-open-in-new</v-icon>
                  </a>
                </div>
              </li>
            </ol>
          </article>
        </section>

        <!-- Panel II: Como presentar (presentar + compensaciones + documentar) -->
        <section
          v-show="activeTab === 'presentar'"
          id="panel-presentar"
          role="tabpanel"
          aria-labelledby="tab-presentar"
          class="dr-panel"
        >
          <article
            id="presentar"
            class="dr-section"
          >
            <div class="dr-section-head">
              <span
                class="dr-section-num"
                aria-hidden="true"
              >04</span>
              <div>
                <h2>{{ t("derechos.s4.h") }}</h2>
                <p class="dr-section-intro">
                  {{ t("derechos.s4.intro") }}
                </p>
              </div>
            </div>

            <div class="dr-paths">
              <div class="dr-path">
                <p class="dr-path-eyebrow">
                  {{ t("derechos.s4.telematica") }}
                </p>
                <p
                  class="dr-path-text"
                  v-html="t('derechos.s4.telematicaCuerpo', { clave: 'cl@ve' })"
                />
              </div>
              <div class="dr-path">
                <p class="dr-path-eyebrow">
                  {{ t("derechos.s4.papel") }}
                </p>
                <p class="dr-path-text">
                  {{ t("derechos.s4.papelCuerpo") }}
                </p>
              </div>
            </div>

            <p class="dr-vias-head">
              {{ t("derechos.s4.viasHead") }}
            </p>
            <ul class="dr-vias">
              <li
                v-for="via in vias"
                :key="via.titulo"
              >
                <v-icon
                  size="20"
                  aria-hidden="true"
                  class="dr-via-icon"
                >
                  {{ via.icon }}
                </v-icon>
                <div>
                  <p class="dr-via-titulo">
                    {{ via.titulo }}
                  </p>
                  <p class="dr-via-detalle">
                    {{ via.detalle }}
                  </p>
                </div>
              </li>
            </ul>
          </article>

          <article
            id="compensaciones"
            class="dr-section"
          >
            <div class="dr-section-head">
              <span
                class="dr-section-num"
                aria-hidden="true"
              >05</span>
              <div>
                <h2>{{ t("derechos.s5.h") }}</h2>
                <p class="dr-section-intro">
                  {{ t("derechos.s5.intro") }}
                </p>
              </div>
            </div>

            <table class="dr-tabla">
              <thead>
                <tr>
                  <th scope="col">
                    {{ t("derechos.s5.thServicio") }}
                  </th>
                  <th scope="col">
                    {{ t("derechos.s5.thCuantia") }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">
                    {{ t("derechos.s5.servicioInternet") }}
                  </th>
                  <td>{{ t("derechos.s5.cuantiaInternet") }}</td>
                </tr>
                <tr>
                  <th scope="row">
                    {{ t("derechos.s5.servicioVoz") }}
                  </th>
                  <td>{{ t("derechos.s5.cuantiaVoz") }}</td>
                </tr>
              </tbody>
            </table>

            <p
              class="dr-legal"
              v-html="t('derechos.s5.legal')"
            />
          </article>

          <article
            id="documentar"
            class="dr-section dr-section--last"
          >
            <div class="dr-section-head">
              <span
                class="dr-section-num"
                aria-hidden="true"
              >06</span>
              <div>
                <h2>{{ t("derechos.s6.h") }}</h2>
                <p class="dr-section-intro">
                  {{ t("derechos.s6.intro") }}
                </p>
              </div>
            </div>

            <ul class="dr-check">
              <li
                v-for="item in itemsDoc"
                :key="item"
              >
                <v-icon
                  size="18"
                  class="dr-check-icon"
                  aria-hidden="true"
                >
                  mdi-checkbox-marked-circle-outline
                </v-icon>
                <span>{{ item }}</span>
              </li>
            </ul>
          </article>
        </section>

        <!-- Panel III: Recursos y dudas (organismos + faq) -->
        <section
          v-show="activeTab === 'recursos'"
          id="panel-recursos"
          role="tabpanel"
          aria-labelledby="tab-recursos"
          class="dr-panel"
        >
          <article
            id="recursos"
            class="dr-section"
          >
            <div class="dr-section-head">
              <span
                class="dr-section-num"
                aria-hidden="true"
              >07</span>
              <div>
                <h2>{{ t("derechos.s7.h") }}</h2>
                <p class="dr-section-intro">
                  {{ t("derechos.s7.intro") }}
                </p>
              </div>
            </div>

            <article
              v-if="recursos.length"
              class="dr-recurso dr-recurso--featured"
            >
              <p class="dr-recurso-label">
                {{ recursos[0].label }}
              </p>
              <p class="dr-recurso-titulo">
                {{ recursos[0].titulo }}
              </p>
              <p class="dr-recurso-desc">
                {{ recursos[0].desc }}
              </p>
              <a
                :href="recursos[0].url"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ recursos[0].linkLabel }}
                <v-icon
                  size="13"
                  aria-hidden="true"
                >mdi-open-in-new</v-icon>
              </a>
            </article>

            <ul class="dr-recursos-secundarios">
              <li
                v-for="r in recursos.slice(1)"
                :key="r.titulo"
              >
                <p class="dr-recurso-label">
                  {{ r.label }}
                </p>
                <p class="dr-recurso-titulo">
                  {{ r.titulo }}
                </p>
                <p class="dr-recurso-desc">
                  {{ r.desc }}
                </p>
                <a
                  :href="r.url"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ r.linkLabel }}
                  <v-icon
                    size="13"
                    aria-hidden="true"
                  >mdi-open-in-new</v-icon>
                </a>
              </li>
            </ul>
          </article>

          <article
            id="faq"
            class="dr-section dr-section--last"
          >
            <div class="dr-section-head">
              <span
                class="dr-section-num"
                aria-hidden="true"
              >08</span>
              <div>
                <h2>{{ t("derechos.s8.h") }}</h2>
              </div>
            </div>

            <div class="dr-faq-list">
              <details
                v-for="(f, idx) in faq"
                :key="f.q"
                class="dr-faq"
                :open="idx === 0"
              >
                <summary class="dr-faq-q">
                  <span>{{ f.q }}</span>
                  <v-icon
                    class="dr-faq-icon"
                    size="22"
                    aria-hidden="true"
                  >
                    mdi-chevron-down
                  </v-icon>
                </summary>
                <p class="dr-faq-a">
                  {{ f.a }}
                </p>
              </details>
            </div>
          </article>
        </section>
      </main>

      <!-- Sidebar sticky con aviso CNMC + acciones (sustituye al aviso/CTA del flujo) -->
      <aside class="dr-sidebar">
        <div class="sidebar-card">
          <div class="sidebar-icon">
            <v-icon
              size="22"
              aria-hidden="true"
            >
              mdi-shield-alert-outline
            </v-icon>
          </div>
          <h2 class="sidebar-title">
            {{ t("derechos.disclaimer.titulo") }}
          </h2>
          <p class="sidebar-text">
            {{ t("derechos.disclaimer.cuerpo") }}
          </p>

          <div class="sidebar-divider" />

          <a
            :href="ENLACE_OFICINA"
            target="_blank"
            rel="noopener noreferrer"
            class="sidebar-action sidebar-action--primary"
          >
            <v-icon
              size="16"
              aria-hidden="true"
            >mdi-open-in-new</v-icon>
            <span>{{ t("derechos.sidebar.sede") }}</span>
          </a>

          <router-link
            :to="rutaTest"
            class="sidebar-action"
          >
            <v-icon
              size="16"
              aria-hidden="true"
            >
              mdi-speedometer
            </v-icon>
            <span>{{ t("derechos.sidebar.test") }}</span>
          </router-link>
        </div>
      </aside>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useHead } from "@unhead/vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

const { t, tm, locale } = useI18n();
const router = useRouter();

const ENLACE_OFICINA = "https://usuariosteleco.digital.gob.es/reclamaciones/telefonia-e-internet";

const rutaTest = computed(() => {
  const name = locale.value === "en" ? "test-en" : "test";
  try {
    return router.resolve({ name }).fullPath;
  } catch {
    return locale.value === "en" ? "/en/speed-test" : "/test-velocidad";
  }
});

// 8 secciones se mantienen como contenido; sirven para el contador del masthead.
const secciones = computed(() => [
  { id: "quien",          titulo: t("derechos.toc.s1") },
  { id: "materias",       titulo: t("derechos.toc.s2") },
  { id: "fases",          titulo: t("derechos.toc.s3") },
  { id: "presentar",      titulo: t("derechos.toc.s4") },
  { id: "compensaciones", titulo: t("derechos.toc.s5") },
  { id: "documentar",     titulo: t("derechos.toc.s6") },
  { id: "recursos",       titulo: t("derechos.toc.s7") },
  { id: "faq",            titulo: t("derechos.toc.s8") },
]);

const tabs = computed(() => [
  { id: "como",      num: "I",   label: t("derechos.tabs.como") },
  { id: "presentar", num: "II",  label: t("derechos.tabs.presentar") },
  { id: "recursos",  num: "III", label: t("derechos.tabs.recursos") },
]);

const activeTab = ref("como");

const fases = computed(() => [
  {
    id: "fase1",
    label: t("derechos.s3.f1.label"),
    titulo: t("derechos.s3.f1.h"),
    meta: [
      { label: t("derechos.s3.f1.metaPlazoLabel"),    value: t("derechos.s3.f1.metaPlazoValor") },
      { label: t("derechos.s3.f1.metaRespuestaLabel"), value: t("derechos.s3.f1.metaRespuestaValor") },
    ],
    parrafos: [
      t("derechos.s3.f1.p1"),
      t("derechos.s3.f1.p2"),
    ],
  },
  {
    id: "fase2",
    label: t("derechos.s3.f2.label"),
    titulo: t("derechos.s3.f2.h"),
    meta: [
      { label: t("derechos.s3.f2.metaPresentLabel"), value: t("derechos.s3.f2.metaPresentValor") },
      { label: t("derechos.s3.f2.metaResolLabel"),   value: t("derechos.s3.f2.metaResolValor") },
      { label: t("derechos.s3.f2.metaCosteLabel"),   value: t("derechos.s3.f2.metaCosteValor") },
    ],
    parrafos: [
      t("derechos.s3.f2.p1"),
      t("derechos.s3.f2.p2"),
    ],
    cta: { texto: t("derechos.s3.f2.cta"), href: ENLACE_OFICINA },
  },
  {
    id: "fase3",
    label: t("derechos.s3.f3.label"),
    titulo: t("derechos.s3.f3.h"),
    parrafos: [
      t("derechos.s3.f3.p1"),
      t("derechos.s3.f3.p2"),
    ],
  },
]);

const vias = computed(() => [
  { icon: "mdi-email-outline",         titulo: t("derechos.s4.via1Titulo"), detalle: t("derechos.s4.via1Detalle") },
  { icon: "mdi-domain",                titulo: t("derechos.s4.via2Titulo"), detalle: t("derechos.s4.via2Detalle") },
  { icon: "mdi-file-document-outline", titulo: t("derechos.s4.via3Titulo"), detalle: t("derechos.s4.via3Detalle") },
]);

const recursos = computed(() => [
  {
    label: t("derechos.s7.r1.label"),
    titulo: t("derechos.s7.r1.titulo"),
    desc: t("derechos.s7.r1.desc"),
    url: ENLACE_OFICINA,
    linkLabel: "usuariosteleco.digital.gob.es",
  },
  {
    label: t("derechos.s7.r2.label"),
    titulo: t("derechos.s7.r2.titulo"),
    desc: t("derechos.s7.r2.desc"),
    url: "https://www.consumo.gob.es/",
    linkLabel: "consumo.gob.es",
  },
  {
    label: t("derechos.s7.r3.label"),
    titulo: t("derechos.s7.r3.titulo"),
    desc: t("derechos.s7.r3.desc"),
    url: "https://www.aecosan.msssi.gob.es/AECOSAN/web/consumo/seccion/oficinas_municipales_consumo.htm",
    linkLabel: t("derechos.s7.r3.linkLabel"),
  },
  {
    label: t("derechos.s7.r4.label"),
    titulo: t("derechos.s7.r4.titulo"),
    desc: t("derechos.s7.r4.desc"),
    url: "https://www.ocu.org/",
    linkLabel: "ocu.org",
  },
]);

// vue-i18n: para arrays/objetos hay que usar `tm()`. `t()` con clave-array
// devuelve la propia clave como string e itera letra a letra.
const tmArray = (key) => {
  const v = tm(key);
  return Array.isArray(v) ? v : [];
};

const itemsSi = computed(() => tmArray("derechos.s2.itemsSi"));
const itemsNo = computed(() => tmArray("derechos.s2.itemsNo"));
const itemsDoc = computed(() => tmArray("derechos.s6.items"));
const faq = computed(() => tmArray("derechos.faq.items"));

useHead({
  title: computed(() => t("seo.titles.derechos")),
  meta: [
    { name: "description", content: computed(() => t("seo.descriptions.derechos")) },
  ],
});
</script>

<style scoped>
/* ============================================================================
   Pagina derechos: layout 2-col (main + sidebar) con tabs editoriales.
   Inspirado en Prensa.vue para mantener coherencia visual y reducir scroll.
   Tokens locales: paper crema + ink navy + accent rojo CNMC.
   ============================================================================ */
.derechos-page {
  --display: ui-serif, Charter, "Bitstream Charter", "Sitka Text", Cambria, Georgia, serif;
  --body: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;

  --ink:        #0b1117;
  --ink-soft:   #1f2937;
  --ink-muted:  #475569;
  --rule:       #d8d4c8;
  --rule-soft:  #ece8db;
  --paper:      #fbfaf6;
  --paper-tint: #f4f1e8;
  --accent:     #c83d2d;
  --accent-soft:#fbebe7;
  --positive:   #2d6a4f;
  --positive-soft: #e6f1ec;

  --s-1: 4px;
  --s-2: 8px;
  --s-3: 12px;
  --s-4: 16px;
  --s-5: 24px;
  --s-6: 32px;
  --s-7: 48px;

  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 100%;
  padding: var(--s-4) 0 var(--s-6);
  font-family: var(--body);
  color: var(--ink);
  background: var(--paper);
}

.derechos-page :where(p, ul, ol, li, h2, h3, table) { font-family: inherit; }

/* ============================================================================
   Masthead compacto (kicker + h1 a la izquierda, meta inline a la derecha)
   ============================================================================ */
.masthead {
  padding: 10px 0 12px;
  border-bottom: 1px solid var(--rule);
  margin-bottom: 14px;
}

.masthead-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--s-4);
  flex-wrap: wrap;
}

.masthead-titles { min-width: 0; }

.masthead-kicker {
  margin: 0 0 4px;
  font-family: var(--body);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--accent);
}

.masthead-title {
  font-family: var(--display);
  font-weight: 700;
  font-size: clamp(1.35rem, 1.6vw + 0.6rem, 1.75rem);
  line-height: 1.15;
  color: var(--ink);
  margin: 0;
  letter-spacing: -0.01em;
}

.masthead-meta {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: var(--s-2);
  font-size: 0.75rem;
  color: var(--ink-muted);
}

.masthead-meta li { margin: 0; }

.masthead-meta strong {
  color: var(--ink);
  font-weight: 700;
  font-variant-numeric: lining-nums tabular-nums;
}

/* ============================================================================
   Layout 2-col
   ============================================================================ */
.dr-layout {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.dr-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.dr-sidebar {
  flex-shrink: 0;
  width: 280px;
  position: sticky;
  top: 88px;
  align-self: flex-start;
}

.dr-lead {
  margin: 0 0 var(--s-4);
  font-family: var(--display);
  font-size: 1rem;
  line-height: 1.55;
  color: var(--ink-soft);
  max-width: 70ch;
}

/* ============================================================================
   Tabs editoriales (mismo patron que Prensa.vue)
   ============================================================================ */
.editorial-tabs {
  display: flex;
  gap: var(--s-2);
  border-bottom: 1px solid var(--rule);
  margin-bottom: var(--s-5);
  overflow-x: auto;
  scrollbar-width: none;
}

.editorial-tabs::-webkit-scrollbar { display: none; }

.editorial-tab {
  appearance: none;
  background: transparent;
  border: 0;
  padding: 14px 16px 12px;
  display: inline-flex;
  align-items: baseline;
  gap: var(--s-2);
  cursor: pointer;
  color: var(--ink-muted);
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  font: inherit;
  white-space: nowrap;
  transition: color 0.2s ease, border-color 0.2s ease;
}

.editorial-tab:hover:not(.is-active) { color: var(--ink); }

.editorial-tab:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: -4px;
  border-radius: 2px;
}

.editorial-tab.is-active {
  color: var(--ink);
  border-bottom-color: var(--accent);
}

.editorial-tab .tab-num {
  font-family: var(--display);
  font-style: italic;
  font-size: 1rem;
  color: var(--accent);
  line-height: 1;
}

.editorial-tab .tab-label {
  font-family: var(--display);
  font-size: 1.0625rem;
  font-weight: 600;
  line-height: 1;
}

.editorial-tab.is-active .tab-label { color: var(--ink); }

/* ============================================================================
   Panel + secciones internas (mas compactas que la version anterior)
   ============================================================================ */
.dr-panel {
  display: flex;
  flex-direction: column;
}

.dr-section {
  padding-bottom: var(--s-5);
  margin-bottom: var(--s-5);
  border-bottom: 1px solid var(--rule-soft);
}

.dr-section--last {
  border-bottom: 0;
  padding-bottom: 0;
  margin-bottom: 0;
}

.dr-section-head {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: var(--s-3);
  align-items: baseline;
  margin-bottom: var(--s-3);
}

.dr-section-num {
  font-family: var(--display);
  font-style: italic;
  font-weight: 400;
  font-size: clamp(1.5rem, 1.6vw + 0.5rem, 2rem);
  line-height: 0.9;
  color: var(--accent);
  font-variant-numeric: lining-nums tabular-nums;
  align-self: start;
}

.dr-section h2 {
  margin: 0 0 var(--s-1);
  font-family: var(--display);
  font-weight: 700;
  font-size: clamp(1.15rem, 0.9vw + 0.85rem, 1.4rem);
  line-height: 1.2;
  letter-spacing: -0.015em;
  color: var(--ink);
}

.dr-section-intro {
  margin: 0;
  font-family: var(--display);
  font-size: 0.9375rem;
  line-height: 1.5;
  color: var(--ink-soft);
  max-width: 70ch;
}

.dr-section h3 {
  margin: 0 0 var(--s-2);
  font-family: var(--display);
  font-weight: 700;
  font-size: 1.0625rem;
  letter-spacing: -0.01em;
  color: var(--ink);
}

.dr-section p {
  margin: 0 0 var(--s-3);
  font-size: 0.9375rem;
  line-height: 1.6;
  color: var(--ink);
  max-width: 70ch;
}

.dr-section a {
  color: var(--accent);
  text-decoration: underline;
  text-underline-offset: 3px;
}

@media (max-width: 600px) {
  .dr-section-head {
    grid-template-columns: 1fr;
    gap: var(--s-2);
  }
}

/* ============================================================================
   01 — Callout
   ============================================================================ */
.dr-callout {
  background: var(--paper-tint);
  border: 1px solid var(--rule);
  border-radius: 10px;
  padding: var(--s-3) var(--s-4);
  max-width: 70ch;
}

.dr-callout-eyebrow {
  margin: 0 0 var(--s-2) !important;
  display: inline-flex;
  align-items: center;
  gap: var(--s-2);
  font-size: 0.6875rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--accent) !important;
}

.dr-callout-eyebrow :deep(.v-icon) { color: var(--accent); }

.dr-callout-text {
  margin: 0 !important;
  font-size: 0.9375rem !important;
  line-height: 1.6 !important;
  color: var(--ink-soft);
}

/* ============================================================================
   02 — Materias (sí / no)
   ============================================================================ */
.dr-materias {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--s-3);
  margin-top: var(--s-3);
}

.dr-materias-col {
  background: var(--paper);
  border: 1px solid var(--rule);
  border-radius: 10px;
  padding: var(--s-3) var(--s-4);
}

.dr-materias-col.is-si { background: var(--positive-soft); border-color: var(--positive-soft); }
.dr-materias-col.is-no { background: var(--accent-soft);   border-color: var(--accent-soft); }

.dr-materias-head {
  margin: 0 0 var(--s-2) !important;
  display: inline-flex;
  align-items: center;
  gap: var(--s-2);
  font-family: var(--body);
  font-size: 0.6875rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink) !important;
}

.dr-materias-col.is-si .dr-materias-head :deep(.v-icon) { color: var(--positive); }
.dr-materias-col.is-no .dr-materias-head :deep(.v-icon) { color: var(--accent); }

.dr-materias-col ul {
  margin: 0;
  padding-left: var(--s-4);
}

.dr-materias-col li {
  margin: 0 0 var(--s-1);
  font-size: 0.8125rem;
  line-height: 1.45;
  color: var(--ink);
}

@media (max-width: 720px) {
  .dr-materias { grid-template-columns: 1fr; }
}

/* ============================================================================
   03 — Timeline (fases)
   ============================================================================ */
.dr-timeline {
  list-style: none;
  margin: var(--s-3) 0 0;
  padding: 0;
  position: relative;
}

.dr-timeline-item {
  display: grid;
  grid-template-columns: 48px 1fr;
  gap: var(--s-3);
  position: relative;
  padding-bottom: var(--s-4);
}

.dr-timeline-item:last-child { padding-bottom: 0; }

.dr-timeline-item::before {
  content: "";
  position: absolute;
  left: 23px;
  top: 38px;
  bottom: 4px;
  width: 1px;
  background: var(--rule);
}

.dr-timeline-item:last-child::before { display: none; }

.dr-timeline-marker {
  width: 48px;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  padding-top: 2px;
}

.dr-timeline-num {
  font-family: var(--display);
  font-weight: 400;
  font-style: italic;
  font-size: 1.75rem;
  line-height: 1;
  color: var(--accent);
  font-variant-numeric: lining-nums tabular-nums;
  background: var(--paper);
  padding: 0 var(--s-1);
}

.dr-timeline-eyebrow {
  margin: 0 0 var(--s-1) !important;
  font-family: var(--body);
  font-size: 0.6875rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-muted);
}

.dr-timeline-meta {
  list-style: none !important;
  padding: 0 !important;
  margin: var(--s-2) 0 var(--s-2) !important;
  display: flex;
  flex-wrap: wrap;
  gap: var(--s-3);
}

.dr-timeline-body p {
  margin-bottom: var(--s-2);
  font-size: 0.875rem;
  line-height: 1.55;
}

.dr-timeline-meta li {
  font-size: 0.75rem;
  color: var(--ink-muted);
  display: inline-flex;
  align-items: baseline;
  gap: var(--s-1);
}

.dr-timeline-meta li::before {
  content: "";
  width: 4px;
  height: 4px;
  border-radius: 999px;
  background: var(--accent);
  display: inline-block;
}

.dr-timeline-meta strong {
  color: var(--ink);
  font-weight: 700;
}

/* ============================================================================
   04 — Como presentar (paths + vias)
   ============================================================================ */
.dr-paths {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--s-3);
  margin: var(--s-3) 0 var(--s-4);
}

.dr-path {
  padding: var(--s-3) var(--s-4);
  background: var(--paper-tint);
  border-radius: 10px;
}

.dr-path-eyebrow {
  margin: 0 0 var(--s-2) !important;
  font-family: var(--body);
  font-size: 0.6875rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--accent) !important;
}

.dr-path-text {
  margin: 0 !important;
  font-size: 0.9375rem !important;
  line-height: 1.6 !important;
  color: var(--ink-soft) !important;
}

@media (max-width: 720px) {
  .dr-paths { grid-template-columns: 1fr; }
}

.dr-vias-head {
  margin: 0 0 var(--s-2) !important;
  font-family: var(--body);
  font-size: 0.6875rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-muted) !important;
}

.dr-vias {
  list-style: none !important;
  padding: 0 !important;
  margin: 0 !important;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--s-2);
}

.dr-vias li {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: var(--s-2);
  align-items: start;
  padding: var(--s-3);
  background: var(--paper-tint);
  border-radius: 8px;
}

@media (max-width: 720px) {
  .dr-vias { grid-template-columns: 1fr; }
}

.dr-via-icon { color: var(--accent) !important; margin-top: 2px; }

.dr-via-titulo {
  margin: 0 !important;
  font-size: 0.9375rem !important;
  font-weight: 700 !important;
  color: var(--ink) !important;
}

.dr-via-detalle {
  margin: 2px 0 0 !important;
  font-size: 0.8125rem !important;
  color: var(--ink-muted) !important;
  line-height: 1.5 !important;
}

/* ============================================================================
   CTA externo (fase 2)
   ============================================================================ */
.dr-cta {
  display: inline-flex;
  align-items: center;
  gap: var(--s-2);
  margin-top: var(--s-2);
  padding: var(--s-2) var(--s-4);
  background: var(--ink);
  color: var(--paper) !important;
  text-decoration: none !important;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  transition: background 200ms cubic-bezier(0.16, 1, 0.3, 1),
              transform 200ms cubic-bezier(0.16, 1, 0.3, 1);
}

.dr-cta:hover {
  background: var(--accent);
  transform: translateY(-1px);
}

.dr-cta :deep(.v-icon) { color: var(--paper); }

/* ============================================================================
   05 — Tabla compensaciones
   ============================================================================ */
.dr-tabla {
  width: 100%;
  border-collapse: collapse;
  margin: var(--s-3) 0 var(--s-3);
  border-top: 2px solid var(--ink);
  border-bottom: 1px solid var(--rule);
  font-size: 0.9375rem;
}

.dr-tabla thead th {
  text-align: left;
  padding: var(--s-2) var(--s-3) var(--s-2) 0;
  font-family: var(--body);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-muted);
  border-bottom: 1px solid var(--rule);
}

.dr-tabla tbody th {
  text-align: left;
  padding: var(--s-3) var(--s-3) var(--s-3) 0;
  font-family: var(--display);
  font-weight: 700;
  font-size: 1rem;
  width: 28%;
  color: var(--ink);
  vertical-align: top;
  border-bottom: 1px solid var(--rule-soft);
}

.dr-tabla tbody td {
  padding: var(--s-3) 0 var(--s-3) var(--s-3);
  vertical-align: top;
  color: var(--ink-soft);
  line-height: 1.55;
  border-bottom: 1px solid var(--rule-soft);
}

.dr-tabla tbody tr:last-child th,
.dr-tabla tbody tr:last-child td { border-bottom: 0; }

.dr-legal {
  margin: var(--s-2) 0 0 !important;
  padding: var(--s-3) var(--s-4);
  background: var(--paper-tint);
  border-radius: 8px;
  font-size: 0.8125rem !important;
  line-height: 1.55 !important;
  color: var(--ink-soft) !important;
  font-style: italic;
}

.dr-legal :deep(strong) { color: var(--ink); font-weight: 700; font-style: normal; }

/* ============================================================================
   06 — Checklist (sin banda CTA: el sidebar la cubre)
   ============================================================================ */
.dr-check {
  list-style: none !important;
  padding: 0 !important;
  margin: var(--s-3) 0 0 !important;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--s-2) var(--s-4);
}

.dr-check li {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: var(--s-2);
  align-items: start;
  padding: var(--s-1) 0;
  font-size: 0.875rem;
  line-height: 1.45;
  color: var(--ink);
}

@media (max-width: 720px) {
  .dr-check { grid-template-columns: 1fr; }
}

.dr-check-icon { color: var(--positive) !important; margin-top: 2px; }

/* ============================================================================
   07 — Recursos (1 destacado + 3 secundarios)
   ============================================================================ */
.dr-recurso {
  background: var(--paper-tint);
  border-radius: 10px;
  padding: var(--s-3) var(--s-4);
  margin: var(--s-3) 0;
}

.dr-recurso--featured {
  background: var(--paper);
  border: 1px solid var(--ink);
  padding: var(--s-4);
}

.dr-recurso-label {
  margin: 0 0 var(--s-1) !important;
  font-family: var(--body);
  font-size: 0.625rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--accent) !important;
}

.dr-recurso--featured .dr-recurso-label { color: var(--accent) !important; }

.dr-recurso-titulo {
  margin: 0 0 var(--s-1) !important;
  font-family: var(--display);
  font-weight: 700 !important;
  color: var(--ink) !important;
  letter-spacing: -0.01em;
  line-height: 1.25;
  font-size: 1rem !important;
}

.dr-recurso--featured .dr-recurso-titulo {
  font-size: 1.125rem !important;
}

.dr-recurso-desc {
  margin: 0 0 var(--s-2) !important;
  font-size: 0.875rem !important;
  color: var(--ink-soft) !important;
  line-height: 1.5 !important;
}

.dr-recurso a {
  display: inline-flex;
  align-items: center;
  gap: var(--s-1);
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--accent);
}

.dr-recurso a :deep(.v-icon) { color: var(--accent); margin-left: 2px; }

.dr-recursos-secundarios {
  list-style: none !important;
  padding: 0 !important;
  margin: var(--s-2) 0 0 !important;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--s-2);
}

.dr-recursos-secundarios li {
  background: var(--paper-tint);
  border-radius: 10px;
  padding: var(--s-3);
}

@media (max-width: 720px) {
  .dr-recursos-secundarios { grid-template-columns: 1fr; }
}

/* ============================================================================
   08 — FAQ
   ============================================================================ */
.dr-faq-list {
  margin-top: var(--s-3);
  border-top: 1px solid var(--rule);
}

.dr-faq {
  border-bottom: 1px solid var(--rule-soft);
}

.dr-faq[open] { background: var(--paper-tint); }

.dr-faq-q {
  cursor: pointer;
  list-style: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--s-3);
  padding: var(--s-3);
  font-family: var(--display);
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--ink);
}

.dr-faq-q::-webkit-details-marker { display: none; }

.dr-faq-icon {
  color: var(--ink-muted) !important;
  transition: transform 250ms cubic-bezier(0.16, 1, 0.3, 1);
  flex-shrink: 0;
}

.dr-faq[open] .dr-faq-icon {
  transform: rotate(180deg);
  color: var(--accent) !important;
}

.dr-faq-a {
  margin: 0 !important;
  padding: 0 var(--s-3) var(--s-3) !important;
  font-size: 0.875rem !important;
  line-height: 1.55 !important;
  color: var(--ink-soft) !important;
  max-width: 70ch;
}

.dr-faq:focus-within {
  outline: 2px solid var(--accent);
  outline-offset: -2px;
}

/* ============================================================================
   Sidebar (mismo patron que Prensa.vue)
   ============================================================================ */
.sidebar-card {
  position: relative;
  overflow: hidden;
  background: var(--paper);
  border: 1px solid var(--rule);
  border-top: 3px solid var(--accent);
  border-radius: 14px;
  padding: 22px 20px;
  color: var(--ink);
  box-shadow: 0 2px 10px rgba(11, 17, 23, 0.06);
}

.sidebar-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid var(--rule);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}

.sidebar-icon :deep(.v-icon) { color: var(--accent); }

.sidebar-title {
  font-family: var(--display);
  font-size: 1.0625rem;
  font-weight: 700;
  color: var(--ink);
  margin: 0 0 6px;
  line-height: 1.3;
  letter-spacing: -0.01em;
}

.sidebar-text {
  font-size: 0.8125rem;
  color: var(--ink-soft);
  line-height: 1.55;
  margin: 0;
}

.sidebar-divider {
  height: 1px;
  background: var(--rule-soft);
  margin: 14px 0;
}

.sidebar-action {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 13px;
  margin-bottom: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  text-decoration: none !important;
  border-radius: 10px;
  background: #fff;
  border: 1px solid var(--rule);
  color: var(--ink) !important;
  transition: border-color 0.2s ease, background 0.2s ease,
              transform 0.2s ease, box-shadow 0.2s ease;
  word-break: break-word;
}

.sidebar-action:last-of-type { margin-bottom: 0; }

.sidebar-action :deep(.v-icon) { color: var(--ink-soft); flex-shrink: 0; }

.sidebar-action:hover {
  background: var(--paper-tint);
  border-color: var(--ink-muted);
  transform: translateX(2px);
}

.sidebar-action:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

.sidebar-action--primary {
  background: var(--ink);
  border-color: var(--ink);
  color: #fff !important;
  box-shadow: 0 4px 14px rgba(11, 17, 23, 0.18);
}

.sidebar-action--primary:hover {
  background: var(--accent);
  border-color: var(--accent);
  box-shadow: 0 6px 18px rgba(200, 61, 45, 0.25);
}

.sidebar-action--primary :deep(.v-icon) { color: #fff; }

/* ============================================================================
   Responsive
   ============================================================================ */
@media (max-width: 960px) {
  .dr-layout {
    flex-direction: column;
  }
  .dr-sidebar {
    width: 100%;
    position: static;
  }
}

@media (max-width: 600px) {
  .derechos-page { padding: 12px 0 20px; }
  .masthead { padding: 10px 0 10px; margin-bottom: 12px; }
  .masthead-row { align-items: flex-start; gap: 6px; }
  .masthead-meta { font-size: 0.6875rem; gap: 6px; }

  .editorial-tabs {
    gap: 4px;
    margin-bottom: 18px;
  }
  .editorial-tab {
    padding: 10px 8px 8px;
    gap: 6px;
    flex: 1;
    min-width: 0;
    justify-content: center;
  }
  .editorial-tab .tab-num { font-size: 0.95rem; }
  .editorial-tab .tab-label { font-size: 0.9375rem; line-height: 1.1; }

  .dr-section { padding-bottom: var(--s-5); margin-bottom: var(--s-5); }
  .dr-section-num { font-size: 1.85rem; }
}

@media (prefers-reduced-motion: reduce) {
  .editorial-tab, .dr-cta, .sidebar-action, .dr-faq-icon {
    transition: none !important;
  }
}
</style>
